import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axiosInstance from "../../../utils/AxiosConfig";
import { DEFAULT_PRODUCT_IMAGE } from "../../../utils/productPresentation";
import { useInitialData } from "../../../SSR/InitialDataContext";
import "../../../Styles/Blogs.css";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";

const formatBlogDate = (value) => {
  if (!value) {
    return "Recently published";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Recently published";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const getBlogSubCategory = (blog) => {
  const value =
    blog?.sub_category ??
    blog?.sub_category_name ??
    blog?.subCategory ??
    blog?.subcategory ??
    "";

  if (value && typeof value === "object") {
    return String(value.name || value.label || "").trim();
  }

  return String(value || "").trim();
};

const decodeHtmlEntities = (value = "") => {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return value;
  }

  const doc = new DOMParser().parseFromString(value, "text/html");
  return doc.documentElement.textContent || doc.body.textContent || "";
};

const looksLikeHtmlMarkup = (value = "") =>
  /<\/?[a-z][\w:-]*(?:\s[^<>]*)?>/i.test(String(value || ""));

const resolveBlogHtml = (value = "") => {
  const normalized = String(value || "");
  if (!normalized || !/&(?:[a-z]+|#\d+|#x[\da-f]+);/i.test(normalized)) {
    return normalized;
  }

  let candidate = normalized;

  for (let index = 0; index < 3; index += 1) {
    const decoded = decodeHtmlEntities(candidate);
    if (decoded === candidate) {
      break;
    }

    if (!looksLikeHtmlMarkup(candidate) && looksLikeHtmlMarkup(decoded)) {
      return decoded;
    }

    candidate = decoded;
  }

  return normalized;
};

function BlogDetail() {
  const { slug } = useParams();
  const initialData = useInitialData();
  const initialBlog =
    initialData?.blogDetail && initialData.blogDetail.slug === slug
      ? initialData.blogDetail
      : null;
  const initialRelatedBlogs = Array.isArray(initialData?.relatedBlogs)
    ? initialData.relatedBlogs
    : [];
  const [blog, setBlog] = useState(initialBlog);
  const [allBlogs, setAllBlogs] = useState(initialRelatedBlogs);
  const [loading, setLoading] = useState(!initialBlog && Boolean(slug));
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [notFound, setNotFound] = useState(!slug);

  useEffect(() => {
    if (initialBlog && initialBlog.slug === slug) {
      setLoading(false);
      setNotFound(false);
      return undefined;
    }

    let isMounted = true;

    const fetchBlog = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const response = await axiosInstance.get(`/blogs/${slug}`);
        if (!isMounted) {
          return;
        }

        setBlog(response.data?.data || null);
        document.title =
          response.data?.data?.meta_title ||
          response.data?.data?.title ||
          "Blogs | Elumia Med Store";
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setBlog(null);
        setNotFound(true);
        document.title = "Blog Not Found | Elumia Med Store";
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (slug) {
      fetchBlog();
    } else {
      setLoading(false);
      setNotFound(true);
    }

    return () => {
      isMounted = false;
    };
  }, [initialBlog, slug]);

  useEffect(() => {
    if (initialRelatedBlogs.length > 0) {
      setRelatedLoading(false);
      return undefined;
    }

    let isMounted = true;

    const fetchAllBlogs = async () => {
      try {
        setRelatedLoading(true);
        const response = await axiosInstance.get("/blogs", {
          params: {
            limit: 100,
          },
        });

        if (!isMounted) {
          return;
        }

        setAllBlogs(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch (error) {
        if (isMounted) {
          setAllBlogs([]);
        }
      } finally {
        if (isMounted) {
          setRelatedLoading(false);
        }
      }
    };

    fetchAllBlogs();

    return () => {
      isMounted = false;
    };
  }, [initialRelatedBlogs.length]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [slug]);

  const sanitizedBlogContent = useMemo(() => {
    return sanitizeHtml(resolveBlogHtml(blog?.content || ""));
  }, [blog?.content]);

  const blogMeta = useMemo(() => {
    if (!blog) {
      return [];
    }

    return [
      blog.author_name || "Elumia Med Store",
      blog.category,
      getBlogSubCategory(blog),
      formatBlogDate(blog.published_at || blog.created_at),
    ].filter(Boolean);
  }, [blog]);

  const sidebarBlogs = useMemo(() => {
    if (!blog || !allBlogs.length) {
      return [];
    }

    const normalizedCategory = String(blog.category || "").trim().toLowerCase();
    const otherBlogs = allBlogs.filter((item) => item?.slug && item.slug !== blog.slug);
    const relatedByCategory = normalizedCategory
      ? otherBlogs.filter(
          (item) =>
            String(item.category || "")
              .trim()
              .toLowerCase() === normalizedCategory,
        )
      : [];

    return (relatedByCategory.length ? relatedByCategory : otherBlogs).slice(0, 5);
  }, [allBlogs, blog]);

  if (loading) {
    return (
      <div className="blogs-page blogs-page--detail">
        <div className="blogs-page__container blogs-page__container--detail">
          <div className="blogs-empty">Loading blog...</div>
        </div>
      </div>
    );
  }

  if (notFound || !blog) {
    return (
      <div className="blogs-page blogs-page--detail">
        <div className="blogs-page__container blogs-page__container--detail">
          <div className="blogs-empty">
            <h2>Blog not found</h2>
            <p>The requested blog is unavailable or no longer published.</p>
            <Link className="blogs-button" to="/blogs">
              Back to Blogs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blogs-page blogs-page--detail">
      <div className="blogs-page__container blogs-page__container--detail">
        <div className="blogs-detail-layout">
          <article className="blogs-detail blogs-detail--main">
            <div className="blogs-detail__header">
              <h1>{blog.title}</h1>
              <div className="blogs-detail__meta">
                {blogMeta.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>

            <div className="blogs-detail__image-wrap">
              <img
                src={blog.image_url || DEFAULT_PRODUCT_IMAGE}
                alt={blog.title || "Blog"}
                className="blogs-detail__image"
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                }}
              />
            </div>

            <div className="blogs-detail__content">
              {sanitizedBlogContent ? (
                <div className="blog-content-wrapper">
                  <div
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: sanitizedBlogContent }}
                  />
                </div>
              ) : (
                <p>No content available for this blog yet.</p>
              )}
            </div>
          </article>

          <aside className="blogs-sidebar" aria-label="Related Articles">
            <div className="blogs-sidebar__inner">
              <h2>Related Articles</h2>
              {relatedLoading ? (
                <div className="blogs-sidebar__empty">Loading related articles...</div>
              ) : sidebarBlogs.length ? (
                <div className="blogs-sidebar__list">
                  {sidebarBlogs.map((item) => (
                    <Link
                      key={item.id || item.slug}
                      className="related-blog-card"
                      to={`/blogs/${item.slug}`}
                    >
                      <img
                        src={item.image_url || DEFAULT_PRODUCT_IMAGE}
                        alt={item.title || "Related article"}
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                        }}
                      />
                      <div className="related-blog-card__body">
                        <h3>{item.title}</h3>
                        <p>{item.category || "Article"}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="blogs-sidebar__empty">
                  No related articles available right now.
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default BlogDetail;
